import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import {
  EquipmentInputType,
  JobStatus,
  StockStore,
} from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { State, storeVisitsToLocal } from "./jobs.store";
import { mergeDateTimeString, toDateString } from "helpers";
import { createCompleteVisitVars } from "operations/mutations/CompleteVisit";
import { fromLocal } from "helpers/fromLocal";
import { Job } from "models/Job";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncMutations = {
  rejectJob: createAppAsyncThunk(
    "jobs/rejectJob",
    async (
      props: { jobId: string; rejectReason: string; noAccess: boolean },
      { rejectWithValue, extra: { sdk } }
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.rejectJobAssignment, {
        thunkName: "jobs/rejectJob",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.rejectJobAssignment) return rejectWithValue("something went wrong");
      return { queued };
    }
  ),
  acceptJob: createAppAsyncThunk(
    "jobs/acceptJob",
    async (props: { jobId: string }, { rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { errors, queued } = await graphqlRequest(sdk.updateJobStatus, {
        thunkName: "jobs/acceptJob",
        thunkProps: props,
        variables: {
          jobId,
          status: JobStatus.Accepted,
        },
      });
      if (errors) return rejectWithValue(errors);
      return { queued };
    }
  ),
  //EditEquipmentDialog
  updateEquipment: createAppAsyncThunk(
    "jobs/updateEquipment",
    async (
      props: { jobId: string; equipment: EquipmentInputType },
      { rejectWithValue, extra: { sdk } }
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateEquipment, {
        thunkName: "jobs/updateEquipment",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateEquipment) return rejectWithValue("something went wrong");
      return { queued };
    }
  ),
  //ChangeJobEquipmentDialog
  changeJobEquipment: createAppAsyncThunk(
    "jobs/changeJobEquipment",
    async (props: { jobId: string; equipmentId: string }, { rejectWithValue, extra: { sdk } }) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.changeJobEquipment, {
        thunkName: "jobs/changeJobEquipment",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.changeJobEquipment) return rejectWithValue("something went wrong");
      return { queued };
    }
  ),
  completeVisit: createAppAsyncThunk(
    "visit/completeVisit",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const {
        jobs: { jobVisits },
        user: { userVar },
      } = getState();
      if (!userVar) return rejectWithValue("No user");
      const visit = jobVisits[jobId];
      const completeVisitVars = createCompleteVisitVars(jobId, userVar.suppliedStoreId, visit);

      const { data, errors, queued } = await graphqlRequest(sdk.completeVisitWithParts, {
        thunkName: "visit/completeVisit",
        thunkProps: props,
        variables: completeVisitVars,
        onError: [{ type: "jobs/resetJobStatus", payload: { jobId } }],
      });

      if (errors) return rejectWithValue(errors);
      if (data && !data.completeVisitWithParts) return rejectWithValue("something went wrong");
      return { queued, followUp: visit.followUp.followUpChecked };
    }
  ),
  startVisitTravel: createAppAsyncThunk(
    "visit/startVisitTravel",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { jobVisits } = getState().jobs;
      let firstTravelTime = jobVisits[jobId].travelTimes[0];
      if (!firstTravelTime) return rejectWithValue("No times");
      let startTime = mergeDateTimeString(firstTravelTime.startDate, firstTravelTime.startTime);

      const { data, errors, queued } = await graphqlRequest(sdk.startVisitTravel, {
        thunkName: "visit/startVisitTravel",
        thunkProps: props,
        variables: { jobId, startTime },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.startVisitTravel) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  startVisitWork: createAppAsyncThunk(
    "visit/startVisitWork",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { jobVisits } = getState().jobs;
      const { travelTimes } = jobVisits[jobId];
      const lastTravelTime = [...travelTimes].pop();
      const now = toDateString(Date.now());
  
      try {
        const { data, errors, queued } = await graphqlRequest(sdk.startVisitWork, {
          thunkName: "visit/startVisitWork",
          thunkProps: props,
          variables: {
            jobId,
            startTime: now,
            travelStopTime: lastTravelTime?.stopTime || now,
          },
        });
  
        if (errors) {
          return rejectWithValue(errors);
        }

        if (data && !data.startVisitWork) {
          const message = "Start Visit Work failed: 'startVisitWork' field missing in data";
          return rejectWithValue(message);
        }
  
        return { data, queued };
  
      } catch (error) {
        return rejectWithValue("An unexpected error occurred");
      }
    }
  ),
  updateETA: createAppAsyncThunk(
    "visit/updateETA",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { jobVisits } = getState().jobs;
      const eta = jobVisits[jobId].travelEta;
      if (!eta) return rejectWithValue("No ETA");
      const { data, errors, queued } = await graphqlRequest(sdk.updateETA, {
        thunkName: "visit/updateETA",
        thunkProps: props,
        variables: {
          jobId,
          eta,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateETA) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  updateVisitInformation: createAppAsyncThunk(
    "visit/updateVisitInformation",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { jobVisits } = getState().jobs;
      const { travelMileage } = jobVisits[jobId];
      const { data, errors, queued } = await graphqlRequest(sdk.updateVisitInformation, {
        thunkName: "visit/updateVisitInformation",
        thunkProps: props,
        variables: {
          jobId,
          miles: travelMileage,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateVisitInformation) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  updateVisitTravel: createAppAsyncThunk(
    "visit/updateVisitTravel",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { jobVisits } = getState().jobs;
      const { travelTimes, travelMileage, errors: vErrors } = jobVisits[jobId];
      let errorKeys = Object.keys(vErrors);
      if (errorKeys.includes("travelTimes") || errorKeys.includes("travelMileage")) {
        return rejectWithValue("Invalid travelTimes or mileage");
      }
      const { data, errors, queued } = await graphqlRequest(sdk.updateVisitTravel, {
        thunkName: "visit/updateVisitTravel",
        thunkProps: props,
        variables: {
          jobId,
          travelTimes: travelTimes.map((tt) => ({
            startTime: mergeDateTimeString(tt.startDate, tt.startTime),
            stopTime:
              tt.stopDate && tt.stopTime ? mergeDateTimeString(tt.stopDate, tt.stopTime) : null,
          })),
          miles: travelMileage,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateVisitTravel) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
};

export const mutationBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncMutations.rejectJob.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.rejectJob.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.rejectJob.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.rejectJob.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.rejectJob.fulfilled, (state, { meta }) => {
    state.jobs[meta.arg.jobId].status = JobStatus.Rejected;
    state.rejectJob.loading = false;
    state.rejectJob.text = "";
    state.rejectJob.open = false;
    return state;
  });
  builder.addCase(asyncMutations.acceptJob.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.jobs[meta.arg.jobId].status = JobStatus.Assigned;
    return state;
  });
  builder.addCase(asyncMutations.acceptJob.fulfilled, (state, { meta }) => {
    state.jobs[meta.arg.jobId].status = JobStatus.Accepted;
    return state;
  });
  // EditEquipmentDialog
  builder.addCase(asyncMutations.updateEquipment.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.updateEquipment.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateEquipment.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.updateEquipment.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.updateEquipment.fulfilled, (state, { meta }) => {
    state.updateEquipment.loading = false;
    state.updateEquipment.open = false;
    const { jobId, equipment } = meta.arg;
    if (state.jobs[jobId].equipment) {
      state.jobs[jobId].equipment!.location = equipment.location;
    }
    return state;
  });
  // ChangeJobEquipmentDialog
  builder.addCase(asyncMutations.changeJobEquipment.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.changeJobEquipment.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.changeJobEquipment.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.changeJobEquipment.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.changeJobEquipment.fulfilled, (state) => {
    state.changeJobEquipment.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.completeVisit.rejected, (state, { payload: errors, meta }) => {
    if (!errors) throw new Error("Something went horribly wrong; error undefined");
    if (typeof errors === "string") throw new Error(errors);

    const id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;

    const { infoString } = ((messages: string[]): { shortMessage: string; infoString?: string } => {
      for (let message of messages) {
        if (!message.includes("|")) continue;
        const [s0] = message.split(" at ");
        const [shortMessage, infoString] = s0.split("|");
        return { shortMessage, infoString };
      }
      return {
        shortMessage: messages[0],
      };
    })(errors.map((e) => e.message));

    if (infoString) {
      const infos = infoString.split(",");
      for (let info of infos) {
        if (info.includes("CompleteVisit")) {
          state.jobVisits[id].newWorkUnitId = parseInt(info.split(":")[1]);
        }

        if (info.includes("UploadFiles")) {
          state.jobVisits[id].files = [];
        }

        if (info.includes("AddParts")) {
          const addedParts = info.split(":")[1]?.split(";");
          const { usedParts } = state.jobVisits[id];
          usedParts.map((p) =>
            addedParts.includes(p.part.id!) && p.part.stockStore !== StockStore.Other
              ? (p.uploaded = true)
              : p
          );
        }

        if(info.includes("CompleteInspection")) {
          const { inspections } = state.jobVisits[id];
          if (inspections.length !== 0) {
            const idList = info.split(":")[1]?.split(";");
            inspections.map((inspection) => {
              let id = null;
              for(let idString of idList) {
                const dashIndex = idString.indexOf('-');
                if(idString.substring(0,dashIndex)=== inspection.specification.id.toString()) {
                  id = idString.substring(dashIndex+1);
                  break;
                }
              }

              let convertedId = null;
              if (!id || id === "null") convertedId = null;
              else convertedId = +id

              return {
                ...inspection,
                deviceHistoryId: convertedId ?? null
              };
            })
          }
        }

        if (info.includes("RequestPart")) {
          const { usedParts } = state.jobVisits[id];
          usedParts.map((p) => (p.part.stockStore === StockStore.Other ? (p.uploaded = true) : p));
        }

        if (info.includes("FillChecklist")) {
          const { checklists } = state.jobVisits[id];
          checklists.map((cl) => (cl.uploaded = true));
        }
      }
    }
  });
  builder.addCase(asyncMutations.completeVisit.pending, (state, { meta }) => {
    if (meta.queued) return state;
    let id = state.selectedJobId;
    if (!id) throw Error("Missing selectedId");
    state.jobVisits[id].submitLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.completeVisit.fulfilled, (state, { meta }) => {
    let id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    localStorage.removeItem("lastLoadedJobs")
    storeVisitsToLocal(state);
        const loadedJobs =
          fromLocal<{
            [x: string]: Job;
          }>("jobs") || {};
          console.log(loadedJobs)
        delete loadedJobs[id];
        localStorage.setItem("jobs", JSON.stringify(loadedJobs))
    return state;
  });
  builder.addCase(asyncMutations.startVisitTravel.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    return state;
  });
  builder.addCase(asyncMutations.startVisitTravel.fulfilled, (state) => {
    return state;
  });
  builder.addCase(asyncMutations.startVisitWork.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    return state;
  });
  builder.addCase(
    asyncMutations.startVisitWork.fulfilled,
    (state, { payload: { data, queued }, meta: { arg } }) => {
      let dateTime: string | null;

      if (!queued) dateTime = toDateString(data?.startVisitWork?.workTimes[0].startTime);
      else dateTime = toDateString(Date.now());

      if (!dateTime) return state;
      state.jobVisits[arg.jobId].workTimes = [
        {
          startDate: dateTime,
          startTime: dateTime,
        },
      ];
      storeVisitsToLocal(state);
      return state;
    }
  );
  builder.addCase(asyncMutations.updateETA.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    return state;
  });
  builder.addCase(asyncMutations.updateETA.fulfilled, (state) => {
    return state;
  });
  builder.addCase(
    asyncMutations.updateVisitInformation.rejected,
    (state, { payload: errors, meta }) => {
      if (meta.aborted) return state;
      return state;
    }
  );
  builder.addCase(asyncMutations.updateVisitInformation.fulfilled, (state) => {
    return state;
  });
  builder.addCase(asyncMutations.updateVisitTravel.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    return state;
  });
  builder.addCase(asyncMutations.updateVisitTravel.fulfilled, (state) => {
    return state;
  });
};
